import React, { useCallback, useMemo } from 'react';
import RSIForm, { RSIConfig } from '../RSI';
import { colors } from '../../../../../colors';
import Checkbox from '../../../../../components/Checkbox';
import InputField from '../../../../../components/Form/InputField';

interface Props {
  useTakeProfitTransactions: boolean;
  setUseTakeProfitTransactions: React.Dispatch<React.SetStateAction<boolean>>;
  oppositeOrderRsiConfig: RSIConfig;
  setOppositeOrderRsiConfig: React.Dispatch<React.SetStateAction<RSIConfig>>;
  sameOrderRsiConfig: RSIConfig;
  setSameOrderRsiConfig: React.Dispatch<React.SetStateAction<RSIConfig>>;
  otherIndicatorConfig: {
    oppositeOrderTakeProfitSize: string;
    sameOrderTakeProfitSize: string;
    oppositeOrderStoplossThreshold: string;
    sameOrderStoplossThreshold: string;
  };
  setOtherIndicatorConfig: React.Dispatch<
    React.SetStateAction<{
      oppositeOrderTakeProfitSize: string;
      sameOrderTakeProfitSize: string;
      oppositeOrderStoplossThreshold: string;
      sameOrderStoplossThreshold: string;
    }>
  >;
}

const IndicatorConfigForm = (props: Props) => {
  const {
    useTakeProfitTransactions,
    setUseTakeProfitTransactions,
    oppositeOrderRsiConfig,
    setOppositeOrderRsiConfig,
    sameOrderRsiConfig,
    setSameOrderRsiConfig,
    otherIndicatorConfig,
    setOtherIndicatorConfig,
  } = props;

  const updateValue = useCallback(
    (key: string, value: string) => {
      setOtherIndicatorConfig((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [setOtherIndicatorConfig],
  );

  const oppositeOrderSize = useMemo(() => {
    return {
      label: 'Opposite Order Transaction Size',
      value: otherIndicatorConfig.oppositeOrderTakeProfitSize,
      handleValueChange: (value: string) =>
        updateValue('oppositeOrderTakeProfitSize', value),
      shouldFit: true,
    };
  }, [otherIndicatorConfig.oppositeOrderTakeProfitSize, updateValue]);

  const sameOrderSize = useMemo(() => {
    return {
      label: 'Same Order Transaction Size',
      value: otherIndicatorConfig.sameOrderTakeProfitSize,
      handleValueChange: (value: string) =>
        updateValue('sameOrderTakeProfitSize', value),
      shouldFit: true,
    };
  }, [otherIndicatorConfig.sameOrderTakeProfitSize, updateValue]);

  const oppositeOrderStopLossThreshold = useMemo(() => {
    return {
      label: 'Opposite Order Stop Loss Threshold',
      value: otherIndicatorConfig.oppositeOrderStoplossThreshold,
      handleValueChange: (value: string) =>
        updateValue('oppositeOrderStoplossThreshold', value),
      shouldFit: true,
    };
  }, [otherIndicatorConfig.oppositeOrderStoplossThreshold, updateValue]);

  const sameOrderStopLossThreshold = useMemo(() => {
    return {
      label: 'Same Order Stop Loss Threshold',
      value: otherIndicatorConfig.sameOrderStoplossThreshold,
      handleValueChange: (value: string) =>
        updateValue('sameOrderStoplossThreshold', value),
      shouldFit: true,
    };
  }, [otherIndicatorConfig.sameOrderStoplossThreshold, updateValue]);

  return (
    <div>
      <p
        style={{
          fontWeight: 'bold',
          color: colors.white,
          marginLeft: 50,
          marginTop: 30,
        }}
      >
        Indicator Config
      </p>
      <div style={{ marginLeft: 50 }}>
        <Checkbox
          label={'Use Take Profit Transactions'}
          value={useTakeProfitTransactions}
          handleValueChange={setUseTakeProfitTransactions}
          color={colors.white}
        />
      </div>

      <RSIForm
        label={'Opposite Order RSI Information'}
        rsiConfig={oppositeOrderRsiConfig}
        setRsiConfig={setOppositeOrderRsiConfig}
      />
      <RSIForm
        label={'Same Order RSI Information'}
        rsiConfig={sameOrderRsiConfig}
        setRsiConfig={setSameOrderRsiConfig}
      />

      <div
        style={{
          marginLeft: 50,
          marginRight: 50,
          marginTop: 0,
          marginBottom: 10,
        }}
      >
        <p style={{ fontWeight: 'bold', color: colors.white }}>
          Take Profit Transaction Config
        </p>
        <div style={{ display: 'flex', columnGap: 16 }}>
          <InputField config={oppositeOrderSize} />
          <InputField config={sameOrderSize} />
          <InputField config={oppositeOrderStopLossThreshold} />
          <InputField config={sameOrderStopLossThreshold} />
        </div>
      </div>
    </div>
  );
};

export default IndicatorConfigForm;
